"use client";

import React, { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import {
  Briefcase,
  Code,
  Users,
  TrendingUp,
  X,
  Upload,
  CheckCircle,
  Mail,
  Phone,
  User,
  Building,
  DollarSign,
  Globe,
  Github,
  Linkedin,
  FileText,
  Calendar,
  ArrowRight,
  Sparkles,
  Rocket,
  Heart,
  Zap,
} from "lucide-react";
import AlertDialog from "@/utils/Alert";
import { createWithMultipart, post } from "@/services/ApiService";
import {
  CREATE_DEVELOPER,
  CREATE_INVESTOR,
  CREATE_JOIN_TEAM,
  CREATE_PARTNER,
} from "@/services/ApiRoutes";
import { useAppSelector } from "@/store/hooks";

type RoleType = "investor" | "developer" | "team" | "partner" | null;

const roles = [
  {
    id: "investor" as RoleType,
    icon: TrendingUp,
    title: "Join as Investor",
    description:
      "Partner with us to drive innovation and growth in the digital economy. Be part of something built on intelligence.",
    gradient: "from-[#8B31FF] to-[#8B31FF]",
    accentColor: "#8B31FF",
  },
  {
    id: "developer" as RoleType,
    icon: Code,
    title: "Join as Developer",
    description:
      "Build cutting-edge solutions with the latest technologies and frameworks. Ship products that scale globally.",
    gradient: "from-[#8B31FF] to-[#8B31FF]",
    accentColor: "#8B31FF",
  },
  {
    id: "team" as RoleType,
    icon: Users,
    title: "Join the Team",
    description:
      "Be part of our creative team in design, marketing, or operations. Grow your career in a fast-paced environment.",
    gradient: "from-[#8B31FF] to-[#D946EF]",
    accentColor: "#D946EF",
  },
  {
    id: "partner" as RoleType,
    icon: Briefcase,
    title: "Become a Partner",
    description:
      "Collaborate with us to expand our reach and create mutual value. Build a long-term strategic relationship.",
    gradient: "from-[#2563EB] to-[#8B31FF]",
    accentColor: "#2563EB",
  },
];

const stats = [
  { icon: Rocket, value: "100+", label: "Projects Delivered" },
  { icon: Users, value: "20+", label: "Team Members" },
  { icon: Heart, value: "98%", label: "Client Retention" },
  { icon: Zap, value: "5+", label: "Years of Excellence" },
];

const RoleCard = ({
  role,
  index,
  mode,
  onClick,
}: {
  role: (typeof roles)[0];
  index: number;
  mode: "light" | "dark";
  onClick: () => void;
}) => {
  const Icon = role.icon;
  return (
    <motion.div
      initial={{ opacity: 0, y: 30 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true }}
      transition={{ duration: 0.5, delay: index * 0.1 }}
      whileHover={{ y: -10 }}
      className="group relative h-full cursor-pointer"
      onClick={onClick}
    >
      <div
        className={`relative h-full flex flex-col justify-between overflow-hidden rounded-[2rem] border backdrop-blur-md p-8 transition-all duration-500 ${
          mode === "dark"
            ? "bg-[#0B0F19]/50 border-white/10 hover:border-[#8B31FF]/30 hover:shadow-[0_0_40px_rgba(79,17,194,0.2)]"
            : "bg-white/70 border-gray-200 hover:border-[#8B31FF]/30 hover:shadow-[0_10px_40px_rgba(79,17,194,0.1)]"
        }`}
      >
        <div className="absolute inset-0 bg-gradient-to-br from-[#8B31FF]/0 via-[#8B31FF]/5 to-[#8B31FF]/5 opacity-0 group-hover:opacity-100 transition-opacity duration-500 rounded-[2rem]" />
        <div
          className={`absolute -right-20 -top-20 h-60 w-60 rounded-full blur-[80px] transition-all duration-500 group-hover:scale-125 ${
            mode === "dark"
              ? "bg-[#8B31FF]/10 group-hover:bg-[#8B31FF]/25"
              : "bg-[#8B31FF]/5 group-hover:bg-[#8B31FF]/12"
          }`}
        />
        <div className="absolute bottom-[-10%] right-[-10%] opacity-[0.03] transition-transform duration-700 group-hover:rotate-12 group-hover:scale-125 pointer-events-none">
          <Icon className="h-64 w-64 text-[#8B31FF]" />
        </div>
        <div className="relative z-10">
          <div className="relative mb-8 inline-flex items-center justify-center">
            <div
              className={`absolute inset-0 blur-xl rounded-full ${
                mode === "dark"
                  ? "bg-[#8B31FF]/20 group-hover:bg-[#8B31FF]/20"
                  : "bg-[#8B31FF]/10 group-hover:bg-[#8B31FF]/20"
              } transition-colors duration-500`}
            />
            <div
              className={`relative h-16 w-16 rounded-lg border flex items-center justify-center shadow-lg transition-colors duration-500 ${
                mode === "dark"
                  ? "bg-[#131620] border-white/10 group-hover:border-[#8B31FF]/50"
                  : "bg-white border-gray-100 group-hover:border-[#8B31FF]/30"
              }`}
            >
              <Icon
                className={`h-8 w-8 transition-colors duration-500 ${
                  mode === "dark"
                    ? "text-[#8B31FF] group-hover:text-white"
                    : "text-[#8B31FF] group-hover:text-[#8B31FF]"
                }`}
              />
            </div>
          </div>
          <h3
            className={`mb-4 text-2xl font-bold transition-colors duration-300 ${
              mode === "dark"
                ? "text-white group-hover:text-[#8B31FF]"
                : "text-gray-900 group-hover:text-[#8B31FF]"
            }`}
          >
            {role.title}
          </h3>
          <p
            className={`leading-relaxed transition-colors duration-300 ${
              mode === "dark"
                ? "text-gray-400 group-hover:text-gray-300"
                : "text-gray-600 group-hover:text-gray-700"
            }`}
          >
            {role.description}
          </p>
        </div>
        <div
          className={`mt-8 pt-6 border-t flex items-center justify-between relative z-10 ${
            mode === "dark" ? "border-white/5" : "border-gray-100"
          }`}
        >
          <span
            className={`text-sm font-semibold tracking-wide uppercase ${
              mode === "dark" ? "text-[#8B31FF]" : "text-[#8B31FF]"
            }`}
          >
            Apply Now
          </span>
          <motion.div
            whileHover={{ x: 5 }}
            className={`flex h-10 w-10 items-center justify-center rounded-full border transition-all duration-300 ${
              mode === "dark"
                ? "bg-white/5 border-white/10 text-white group-hover:bg-[#8B31FF] group-hover:border-[#8B31FF] group-hover:text-black"
                : "bg-gray-50 border-gray-200 text-gray-700 group-hover:bg-[#8B31FF] group-hover:border-[#8B31FF] group-hover:text-white"
            }`}
          >
            <ArrowRight className="h-4 w-4" />
          </motion.div>
        </div>
      </div>
    </motion.div>
  );
};

const inputBase = (mode: "light" | "dark") =>
  `w-full px-4 py-3 rounded-lg border-2 outline-none transition-all duration-200 text-sm ${
    mode === "dark"
      ? "bg-[#0B0F19] border-white/10 text-white placeholder:text-gray-500 focus:border-[#8B31FF]"
      : "bg-white border-gray-200 text-gray-900 placeholder:text-gray-400 focus:border-[#8B31FF]"
  }`;

const labelBase = (mode: "light" | "dark") =>
  `block text-xs font-semibold uppercase tracking-wider mb-2 ${
    mode === "dark" ? "text-gray-400" : "text-gray-500"
  }`;

const ApplicationModal = ({
  roleType,
  mode,
  onClose,
}: {
  roleType: RoleType;
  mode: "light" | "dark";
  onClose: () => void;
}) => {
  const role = roles.find((r) => r.id === roleType)!;
  const Icon = role.icon;
  const [isSubmitted, setIsSubmitted] = useState(false);

  const [formData, setFormData] = useState({
    fullName: "",
    email: "",
    phoneNumber: "",
    location: "",
    yearsOfExperience: "",
    linkedInProfile: "",
    githubProfile: "",
    portfolioWebsite: "",
    investmentInterest: "",
    companyOrganization: "",
    positionOfInterest: "",
    primaryExpertise: "",
    partnershipType: "",
    companyName: "",
    companyWebsite: "",
    additionalInformation: "",
  });
  const [resume, setResume] = useState<File | null>(null);
  const [fileName, setFileName] = useState("");

  const handleChange = (
    e: React.ChangeEvent<
      HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement
    >,
  ) => setFormData({ ...formData, [e.target.name]: e.target.value });

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files?.[0]) {
      setResume(e.target.files[0]);
      setFileName(e.target.files[0].name);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    AlertDialog(
      "Confirm Submission",
      "Please review your information before submitting. Are you sure you want to proceed?",
      "question",
      0,
      true,
      true,
      "Submit",
      "Cancel",
      async () => {
        try {
          if (roleType === "developer") {
            const data = new FormData();
            Object.keys(formData).forEach((key) => {
              if (formData[key as keyof typeof formData])
                data.append(key, formData[key as keyof typeof formData]);
            });
            if (resume) data.append("resume", resume);
            await createWithMultipart(CREATE_DEVELOPER, data);
          } else if (roleType === "investor") {
            await post(CREATE_INVESTOR, formData);
          } else if (roleType === "team") {
            const data = new FormData();
            Object.keys(formData).forEach((key) => {
              if (formData[key as keyof typeof formData])
                data.append(key, formData[key as keyof typeof formData]);
            });
            if (resume) data.append("resume", resume);
            await createWithMultipart(CREATE_JOIN_TEAM, data);
          } else if (roleType === "partner") {
            await post(CREATE_PARTNER, formData);
          }
          AlertDialog(
            "Success",
            "Your application has been submitted successfully!",
            "success",
            2000,
          );
          setIsSubmitted(true);
        } catch {
          AlertDialog(
            "Error",
            "Something went wrong. Please try again later.",
            "error",
            2000,
          );
        }
      },
      () => {},
    );
  };

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      className="fixed inset-0 z-50 flex items-center justify-center p-4"
      style={{
        backdropFilter: "blur(12px)",
        backgroundColor: "rgba(0,0,0,0.65)",
      }}
      onClick={onClose}
    >
      <motion.div
        initial={{ opacity: 0, scale: 0.92, y: 24 }}
        animate={{ opacity: 1, scale: 1, y: 0 }}
        exit={{ opacity: 0, scale: 0.92, y: 24 }}
        transition={{ duration: 0.3, ease: "easeOut" }}
        className={`relative w-full max-w-2xl max-h-[90vh] rounded-[2rem] overflow-hidden shadow-2xl flex flex-col ${
          mode === "dark"
            ? "bg-[#0D0F1A] border border-white/10"
            : "bg-white border border-gray-100"
        }`}
        onClick={(e) => e.stopPropagation()}
      >
        <div
          className={`relative overflow-hidden px-8 pt-8 pb-6 flex-shrink-0 bg-gradient-to-br ${role.gradient}`}
        >
          <div className="absolute inset-0 bg-black/20" />
          <div className="absolute -top-16 -right-16 w-48 h-48 rounded-full bg-white/5 blur-2xl" />
          <div className="relative z-10 flex items-start justify-between gap-4">
            <div className="flex items-center gap-4">
              <div className="h-14 w-14 rounded-lg bg-white/15 border border-white/20 flex items-center justify-center flex-shrink-0">
                <Icon className="h-7 w-7 text-white" />
              </div>
              <div>
                <h2 className="text-2xl font-bold text-white leading-tight">
                  {role.title}
                </h2>
                <p className="text-white/70 text-sm mt-0.5">
                  Fill in the details below to apply
                </p>
              </div>
            </div>
            <button
              onClick={onClose}
              className="flex-shrink-0 h-9 w-9 rounded-lg bg-white/15 hover:bg-white/25 border border-white/20 flex items-center justify-center transition-colors"
            >
              <X className="h-4 w-4 text-white" />
            </button>
          </div>
        </div>

        <div className="overflow-y-auto flex-1 px-8 py-6">
          {isSubmitted ? (
            <motion.div
              initial={{ opacity: 0, scale: 0.9 }}
              animate={{ opacity: 1, scale: 1 }}
              className="flex flex-col items-center justify-center py-12 text-center"
            >
              <div className="h-20 w-20 rounded-full bg-gradient-to-br from-[#8B31FF] to-[#8B31FF] flex items-center justify-center mb-6 shadow-lg">
                <CheckCircle className="h-10 w-10 text-white" />
              </div>
              <h3
                className={`text-2xl font-bold mb-3 ${mode === "dark" ? "text-white" : "text-gray-900"}`}
              >
                Application Submitted!
              </h3>
              <p
                className={`text-sm leading-relaxed max-w-sm mb-8 ${mode === "dark" ? "text-gray-400" : "text-gray-500"}`}
              >
                Thank you for your interest in joining Hubmicroo. Our team will
                review your application and get back to you within 3–5 business
                days.
              </p>
              <button
                onClick={onClose}
                className="px-8 py-3 rounded-lg bg-gradient-to-r from-[#8B31FF] to-[#8B31FF] text-white font-semibold text-sm hover:shadow-lg hover:shadow-[#8B31FF]/30 transition-shadow"
              >
                Close
              </button>
            </motion.div>
          ) : (
            <form onSubmit={handleSubmit} className="space-y-5">
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div>
                  <label className={labelBase(mode)}>
                    <User className="w-3 h-3 inline mr-1.5" />
                    Full Name *
                  </label>
                  <input
                    type="text"
                    name="fullName"
                    required
                    value={formData.fullName}
                    onChange={handleChange}
                    placeholder="John Doe"
                    className={inputBase(mode)}
                  />
                </div>
                <div>
                  <label className={labelBase(mode)}>
                    <Mail className="w-3 h-3 inline mr-1.5" />
                    Email Address *
                  </label>
                  <input
                    type="email"
                    name="email"
                    required
                    value={formData.email}
                    onChange={handleChange}
                    placeholder="john@example.com"
                    className={inputBase(mode)}
                  />
                </div>
                <div>
                  <label className={labelBase(mode)}>
                    <Phone className="w-3 h-3 inline mr-1.5" />
                    Phone Number *
                  </label>
                  <input
                    type="tel"
                    name="phoneNumber"
                    required
                    value={formData.phoneNumber}
                    onChange={handleChange}
                    placeholder="+1 (555) 000-0000"
                    className={inputBase(mode)}
                  />
                </div>
                <div>
                  <label className={labelBase(mode)}>
                    <Globe className="w-3 h-3 inline mr-1.5" />
                    Location *
                  </label>
                  <input
                    type="text"
                    name="location"
                    required
                    value={formData.location}
                    onChange={handleChange}
                    placeholder="City, Country"
                    className={inputBase(mode)}
                  />
                </div>
              </div>

              <div
                className={`border-t ${mode === "dark" ? "border-white/5" : "border-gray-100"}`}
              />

              {roleType === "investor" && (
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <div>
                    <label className={labelBase(mode)}>
                      <Building className="w-3 h-3 inline mr-1.5" />
                      Company / Organization
                    </label>
                    <input
                      type="text"
                      name="companyOrganization"
                      value={formData.companyOrganization}
                      onChange={handleChange}
                      placeholder="Investment Firm Name"
                      className={inputBase(mode)}
                    />
                  </div>
                  <div>
                    <label className={labelBase(mode)}>
                      <DollarSign className="w-3 h-3 inline mr-1.5" />
                      Investment Interest *
                    </label>
                    <select
                      name="investmentInterest"
                      required
                      value={formData.investmentInterest}
                      onChange={handleChange}
                      className={inputBase(mode)}
                    >
                      <option value="">Select Range</option>
                      <option value="Seed ($10k - $50k)">
                        Seed ($10k – $50k)
                      </option>
                      <option value="Early Stage ($50k - $200k)">
                        Early Stage ($50k – $200k)
                      </option>
                      <option value="Growth Stage ($200k+)">
                        Growth Stage ($200k+)
                      </option>
                      <option value="Other">Other</option>
                    </select>
                  </div>
                  <div className="sm:col-span-2">
                    <label className={labelBase(mode)}>
                      <Linkedin className="w-3 h-3 inline mr-1.5" />
                      LinkedIn Profile
                    </label>
                    <input
                      type="url"
                      name="linkedInProfile"
                      value={formData.linkedInProfile}
                      onChange={handleChange}
                      placeholder="https://linkedin.com/in/yourprofile"
                      className={inputBase(mode)}
                    />
                  </div>
                </div>
              )}

              {roleType === "developer" && (
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <div>
                    <label className={labelBase(mode)}>
                      <Calendar className="w-3 h-3 inline mr-1.5" />
                      Years of Experience *
                    </label>
                    <select
                      name="yearsOfExperience"
                      required
                      value={formData.yearsOfExperience}
                      onChange={handleChange}
                      className={inputBase(mode)}
                    >
                      <option value="">Select Experience</option>
                      <option value="0-1 years">0–1 years</option>
                      <option value="1-3 years">1–3 years</option>
                      <option value="3-5 years">3–5 years</option>
                      <option value="5+ years">5+ years</option>
                    </select>
                  </div>
                  <div>
                    <label className={labelBase(mode)}>
                      <Code className="w-3 h-3 inline mr-1.5" />
                      Primary Expertise *
                    </label>
                    <select
                      name="primaryExpertise"
                      required
                      value={formData.primaryExpertise}
                      onChange={handleChange}
                      className={inputBase(mode)}
                    >
                      <option value="">Select Expertise</option>
                      <option value="Frontend">Frontend Development</option>
                      <option value="Backend">Backend Development</option>
                      <option value="Full Stack">Full Stack Development</option>
                      <option value="Mobile">Mobile Development</option>
                      <option value="DevOps">DevOps / Cloud</option>
                      <option value="AI/ML">AI / ML Engineering</option>
                    </select>
                  </div>
                  <div>
                    <label className={labelBase(mode)}>
                      <Github className="w-3 h-3 inline mr-1.5" />
                      GitHub Profile
                    </label>
                    <input
                      type="url"
                      name="githubProfile"
                      value={formData.githubProfile}
                      onChange={handleChange}
                      placeholder="https://github.com/yourusername"
                      className={inputBase(mode)}
                    />
                  </div>
                  <div>
                    <label className={labelBase(mode)}>
                      <Globe className="w-3 h-3 inline mr-1.5" />
                      Portfolio Website
                    </label>
                    <input
                      type="url"
                      name="portfolioWebsite"
                      value={formData.portfolioWebsite}
                      onChange={handleChange}
                      placeholder="https://yourportfolio.com"
                      className={inputBase(mode)}
                    />
                  </div>
                  <div className="sm:col-span-2">
                    <label className={labelBase(mode)}>
                      <FileText className="w-3 h-3 inline mr-1.5" />
                      Upload Resume / CV *
                    </label>
                    <div className="relative">
                      <input
                        type="file"
                        onChange={handleFileChange}
                        accept=".pdf,.doc,.docx"
                        className="hidden"
                        id="resume-upload"
                        required
                      />
                      <label
                        htmlFor="resume-upload"
                        className={`flex items-center justify-center gap-3 w-full px-4 py-3.5 rounded-lg border-2 border-dashed cursor-pointer transition-all duration-200 text-sm ${
                          mode === "dark"
                            ? "border-white/15 hover:border-[#8B31FF]/60 text-gray-400 hover:text-[#8B31FF] bg-[#0B0F19]"
                            : "border-gray-200 hover:border-[#8B31FF]/50 text-gray-500 hover:text-[#8B31FF] bg-gray-50"
                        }`}
                      >
                        <Upload className="w-4 h-4" />
                        <span>
                          {fileName || "Click to upload PDF, DOC, or DOCX"}
                        </span>
                      </label>
                    </div>
                  </div>
                </div>
              )}

              {roleType === "team" && (
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <div>
                    <label className={labelBase(mode)}>
                      <Briefcase className="w-3 h-3 inline mr-1.5" />
                      Position of Interest *
                    </label>
                    <select
                      name="positionOfInterest"
                      required
                      value={formData.positionOfInterest}
                      onChange={handleChange}
                      className={inputBase(mode)}
                    >
                      <option value="">Select Position</option>
                      <option value="UI/UX Designer">UI / UX Designer</option>
                      <option value="Graphic Designer">Graphic Designer</option>
                      <option value="Marketing Specialist">
                        Marketing Specialist
                      </option>
                      <option value="Content Creator">Content Creator</option>
                      <option value="Sales Executive">Sales Executive</option>
                      <option value="HR Manager">HR Manager</option>
                      <option value="Operations Manager">
                        Operations Manager
                      </option>
                      <option value="Other">Other</option>
                    </select>
                  </div>
                  <div>
                    <label className={labelBase(mode)}>
                      <Calendar className="w-3 h-3 inline mr-1.5" />
                      Years of Experience *
                    </label>
                    <select
                      name="yearsOfExperience"
                      required
                      value={formData.yearsOfExperience}
                      onChange={handleChange}
                      className={inputBase(mode)}
                    >
                      <option value="">Select Experience</option>
                      <option value="0-1 years">0–1 years</option>
                      <option value="1-3 years">1–3 years</option>
                      <option value="3-5 years">3–5 years</option>
                      <option value="5+ years">5+ years</option>
                    </select>
                  </div>
                  <div>
                    <label className={labelBase(mode)}>
                      <Linkedin className="w-3 h-3 inline mr-1.5" />
                      LinkedIn Profile
                    </label>
                    <input
                      type="url"
                      name="linkedInProfile"
                      value={formData.linkedInProfile}
                      onChange={handleChange}
                      placeholder="https://linkedin.com/in/yourprofile"
                      className={inputBase(mode)}
                    />
                  </div>
                  <div>
                    <label className={labelBase(mode)}>
                      <Globe className="w-3 h-3 inline mr-1.5" />
                      Portfolio Website
                    </label>
                    <input
                      type="url"
                      name="portfolioWebsite"
                      value={formData.portfolioWebsite}
                      onChange={handleChange}
                      placeholder="https://yourportfolio.com"
                      className={inputBase(mode)}
                    />
                  </div>
                  <div className="sm:col-span-2">
                    <label className={labelBase(mode)}>
                      <FileText className="w-3 h-3 inline mr-1.5" />
                      Upload Resume / CV *
                    </label>
                    <div className="relative">
                      <input
                        type="file"
                        onChange={handleFileChange}
                        accept=".pdf,.doc,.docx"
                        className="hidden"
                        id="resume-upload-team"
                        required
                      />
                      <label
                        htmlFor="resume-upload-team"
                        className={`flex items-center justify-center gap-3 w-full px-4 py-3.5 rounded-lg border-2 border-dashed cursor-pointer transition-all duration-200 text-sm ${
                          mode === "dark"
                            ? "border-white/15 hover:border-[#8B31FF]/60 text-gray-400 hover:text-[#8B31FF] bg-[#0B0F19]"
                            : "border-gray-200 hover:border-[#8B31FF]/50 text-gray-500 hover:text-[#8B31FF] bg-gray-50"
                        }`}
                      >
                        <Upload className="w-4 h-4" />
                        <span>
                          {fileName || "Click to upload PDF, DOC, or DOCX"}
                        </span>
                      </label>
                    </div>
                  </div>
                </div>
              )}

              {roleType === "partner" && (
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <div>
                    <label className={labelBase(mode)}>
                      <Building className="w-3 h-3 inline mr-1.5" />
                      Company Name *
                    </label>
                    <input
                      type="text"
                      name="companyName"
                      required
                      value={formData.companyName}
                      onChange={handleChange}
                      placeholder="Your Company Name"
                      className={inputBase(mode)}
                    />
                  </div>
                  <div>
                    <label className={labelBase(mode)}>
                      <Briefcase className="w-3 h-3 inline mr-1.5" />
                      Partnership Type *
                    </label>
                    <select
                      name="partnershipType"
                      required
                      value={formData.partnershipType}
                      onChange={handleChange}
                      className={inputBase(mode)}
                    >
                      <option value="">Select Type</option>
                      <option value="Reseller">Reseller Partnership</option>
                      <option value="Technology">Technology Partnership</option>
                      <option value="Strategic Alliance">
                        Strategic Alliance
                      </option>
                      <option value="Affiliate Program">
                        Affiliate Program
                      </option>
                      <option value="Other">Other</option>
                    </select>
                  </div>
                  <div>
                    <label className={labelBase(mode)}>
                      <Globe className="w-3 h-3 inline mr-1.5" />
                      Company Website
                    </label>
                    <input
                      type="url"
                      name="companyWebsite"
                      value={formData.companyWebsite}
                      onChange={handleChange}
                      placeholder="https://yourcompany.com"
                      className={inputBase(mode)}
                    />
                  </div>
                  <div>
                    <label className={labelBase(mode)}>
                      <Linkedin className="w-3 h-3 inline mr-1.5" />
                      LinkedIn Profile
                    </label>
                    <input
                      type="url"
                      name="linkedInProfile"
                      value={formData.linkedInProfile}
                      onChange={handleChange}
                      placeholder="https://linkedin.com/company/yourcompany"
                      className={inputBase(mode)}
                    />
                  </div>
                </div>
              )}

              <div>
                <label className={labelBase(mode)}>
                  Additional Information *
                </label>
                <textarea
                  name="additionalInformation"
                  required
                  rows={4}
                  value={formData.additionalInformation}
                  onChange={handleChange}
                  placeholder={
                    roleType === "investor"
                      ? "Tell us about your investment philosophy and why you're interested in Hubmicroo..."
                      : roleType === "developer"
                        ? "Tell us about your technical skills, projects, and why you want to join our team..."
                        : roleType === "team"
                          ? "Tell us about your skills, achievements, and why you'd be a great fit..."
                          : "Tell us about your company, partnership goals, and how we can create mutual value..."
                  }
                  className={`${inputBase(mode)} resize-none`}
                />
              </div>

              <motion.button
                type="submit"
                whileHover={{ scale: 1.02 }}
                whileTap={{ scale: 0.98 }}
                className={`w-full py-4 rounded-lg font-semibold text-sm text-white bg-gradient-to-r ${role.gradient} shadow-lg hover:shadow-xl hover:shadow-[#8B31FF]/30 transition-shadow`}
              >
                Submit Application
              </motion.button>
            </form>
          )}
        </div>
      </motion.div>
    </motion.div>
  );
};

const CareersClient = () => {
  const { mode } = useAppSelector((state) => state.theme);
  const [selectedRole, setSelectedRole] = useState<RoleType>(null);

  return (
    <main
      className={`relative min-h-screen overflow-hidden transition-colors duration-700 ${
        mode === "dark"
          ? "bg-[#030712]"
          : "bg-gradient-to-b from-white via-slate-50 to-white"
      }`}
    >
      <div className="absolute top-0 left-0 right-0 h-px bg-gradient-to-r from-transparent via-[#8B31FF] to-transparent opacity-70" />
      <div className="absolute top-0 left-0 right-0 h-[2px] bg-gradient-to-r from-transparent via-[#8B31FF]/40 to-transparent" />

      <div className="absolute inset-0 overflow-hidden pointer-events-none">
        {mode === "dark" && (
          <div className="absolute inset-0 bg-[radial-gradient(ellipse_90%_55%_at_50%_0%,rgba(79,17,194,0.22),transparent)]" />
        )}
        <div
          className={`absolute inset-0 transition-opacity duration-700 ${
            mode === "dark" ? "opacity-[0.055]" : "opacity-[0.03]"
          }`}
          style={{
            backgroundImage:
              mode === "dark"
                ? `linear-gradient(#fff 1px, transparent 1px), linear-gradient(90deg, #fff 1px, transparent 1px)`
                : `linear-gradient(#000 1px, transparent 1px), linear-gradient(90deg, #000 1px, transparent 1px)`,
            backgroundSize: "40px 40px",
          }}
        />
        <motion.div
          className={`absolute -top-32 -right-32 w-[560px] h-[560px] rounded-full blur-[120px] ${
            mode === "dark" ? "bg-[#8B31FF]/40" : "bg-[#8B31FF]/10"
          }`}
          animate={{ x: [0, -45, 0], y: [0, 35, 0], scale: [1, 1.12, 1] }}
          transition={{ duration: 16, repeat: Infinity, ease: "easeInOut" }}
        />
        <motion.div
          className={`absolute -bottom-32 -left-32 w-[520px] h-[520px] rounded-full blur-[120px] ${
            mode === "dark" ? "bg-[#8B31FF]/22" : "bg-[#8B31FF]/8"
          }`}
          animate={{ x: [0, 55, 0], y: [0, -35, 0], scale: [1, 1.18, 1] }}
          transition={{ duration: 20, repeat: Infinity, ease: "easeInOut" }}
        />
        <motion.div
          className={`absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[420px] h-[420px] rounded-full blur-[150px] ${
            mode === "dark" ? "bg-[#D946EF]/12" : "bg-[#8B31FF]/5"
          }`}
          animate={{ scale: [1, 1.25, 1], opacity: [0.5, 1, 0.5] }}
          transition={{ duration: 9, repeat: Infinity, ease: "easeInOut" }}
        />
        {mode === "dark" &&
          [
            { left: "8%", top: "15%", delay: 0 },
            { left: "22%", top: "65%", delay: 0.7 },
            { left: "45%", top: "8%", delay: 1.2 },
            { left: "62%", top: "80%", delay: 0.4 },
            { left: "76%", top: "35%", delay: 1.0 },
            { left: "92%", top: "58%", delay: 1.6 },
          ].map((p, i) => (
            <motion.span
              key={i}
              className="absolute w-[3px] h-[3px] rounded-full bg-[#8B31FF]/60"
              style={{ left: p.left, top: p.top }}
              animate={{ y: [0, -18, 0], opacity: [0.25, 0.75, 0.25] }}
              transition={{
                duration: 3.5 + i * 0.4,
                repeat: Infinity,
                ease: "easeInOut",
                delay: p.delay,
              }}
            />
          ))}
      </div>

      <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pt-32 pb-24">
        <div className="text-center mb-20">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5 }}
            className={`inline-flex items-center gap-2 px-4 py-1.5 rounded-full border mb-8 transition-all duration-300 ${
              mode === "dark"
                ? "border-[#8B31FF]/30 bg-[#8B31FF]/10 backdrop-blur-md"
                : "border-[#8B31FF]/20 bg-white/50"
            }`}
          >
            <span className="relative flex h-2 w-2">
              <span
                className={`animate-ping absolute inline-flex h-full w-full rounded-full opacity-75 ${
                  mode === "dark" ? "bg-[#8B31FF]" : "bg-[#8B31FF]"
                }`}
              />
              <span
                className={`relative inline-flex rounded-full h-2 w-2 ${
                  mode === "dark" ? "bg-[#8B31FF]" : "bg-[#8B31FF]"
                }`}
              />
            </span>
            <span
              className={`text-xs font-semibold tracking-widest uppercase ${
                mode === "dark" ? "text-[#8B31FF]" : "text-[#8B31FF]"
              }`}
            >
              <Sparkles className="w-3 h-3 inline mr-1" />
              Careers at Hubmicroo
            </span>
          </motion.div>

          <motion.h1
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.1 }}
            className={`text-3xl sm:text-4xl md:text-5xl lg:text-6xl font-extrabold mb-4 sm:mb-6 tracking-tight leading-[1.12] ${
              mode === "dark" ? "text-white" : "text-gray-900"
            }`}
          >
            Join Our{" "}
            <span className="text-transparent bg-clip-text bg-gradient-to-r from-[#8B31FF] to-[#8B31FF]">
              Growing Team
            </span>
          </motion.h1>

          <motion.p
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.2 }}
            className={`text-lg max-w-2xl mx-auto leading-relaxed ${
              mode === "dark" ? "text-gray-400" : "text-gray-600"
            }`}
          >
            Whether you&apos;re an investor, developer, creative professional,
            or business partner — we have exciting opportunities for you to grow
            with us.
          </motion.p>
        </div>

        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.5, delay: 0.1 }}
          className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-20"
        >
          {stats.map((stat, i) => {
            const Icon = stat.icon;
            return (
              <div
                key={i}
                className={`rounded-lg border p-6 text-center transition-all duration-300 ${
                  mode === "dark"
                    ? "bg-[#0B0F19]/50 border-white/5 hover:border-[#8B31FF]/30"
                    : "bg-white/70 border-gray-100 hover:border-[#8B31FF]/20"
                }`}
              >
                <Icon
                  className={`h-6 w-6 mx-auto mb-3 ${
                    mode === "dark" ? "text-[#8B31FF]" : "text-[#8B31FF]"
                  }`}
                />
                <div
                  className={`text-3xl font-bold mb-1 ${
                    mode === "dark" ? "text-white" : "text-gray-900"
                  }`}
                >
                  {stat.value}
                </div>
                <div className="text-xs text-gray-500 font-medium">
                  {stat.label}
                </div>
              </div>
            );
          })}
        </motion.div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
          {roles.map((role, index) => (
            <RoleCard
              key={role.id}
              role={role}
              index={index}
              mode={mode}
              onClick={() => setSelectedRole(role.id)}
            />
          ))}
        </div>

        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6 }}
          className={`mt-20 rounded-[2rem] border p-10 text-center overflow-hidden relative ${
            mode === "dark"
              ? "bg-gradient-to-br from-[#0D0F1A] to-[#0B0F19] border-white/10"
              : "bg-gradient-to-br from-white to-slate-50 border-gray-100"
          }`}
        >
          <div className="absolute inset-0 bg-gradient-to-r from-[#8B31FF]/5 via-[#8B31FF]/5 to-[#8B31FF]/5" />
          <div className="relative z-10">
            <h2
              className={`text-3xl md:text-4xl font-bold mb-4 ${
                mode === "dark" ? "text-white" : "text-gray-900"
              }`}
            >
              Don&apos;t see the right fit?
            </h2>
            <p
              className={`text-base mb-8 max-w-lg mx-auto ${
                mode === "dark" ? "text-gray-400" : "text-gray-600"
              }`}
            >
              We&apos;re always looking for talented people. Send us your
              profile and we&apos;ll reach out when the right opportunity comes
              up.
            </p>
            <motion.a
              href="mailto:hubmicroo@gmail.com"
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
              className="inline-flex items-center gap-2 px-8 py-4 rounded-lg bg-gradient-to-r from-[#8B31FF] to-[#8B31FF] text-white font-semibold text-sm shadow-lg hover:shadow-[#8B31FF]/40 hover:shadow-xl transition-shadow"
            >
              <Mail className="h-4 w-4" />
              hubmicroo@gmail.com
            </motion.a>
          </div>
        </motion.div>
      </div>

      <AnimatePresence>
        {selectedRole && (
          <ApplicationModal
            roleType={selectedRole}
            mode={mode}
            onClose={() => setSelectedRole(null)}
          />
        )}
      </AnimatePresence>
    </main>
  );
};

export default CareersClient;
